import { formatPesoAmount } from './money';
import { CLIENT_TYPE_OPTIONS, getClientTypeLabel, normalizeProductFormatCode } from './clientType';
import { orderFieldSearchResults } from './productSearch';

export const CLIENT_PRICING_FALLBACK_IMAGE = 'https://placehold.co/200';
export const CLIENT_PRICING_MODE_COST_PLUS = 'cost_plus';
export const CLIENT_PRICING_MODE_DISCOUNT = 'discount';
export const CLIENT_PRICING_MODE_MARGIN_DISCOUNT = 'margin_discount';
export const CLIENT_PRICING_RULE_FIXED = 'fixed_discount';
export const CLIENT_PRICING_RULE_MARGIN = 'margin_discount';
export const CLIENT_PRICING_MARGIN_DISPLAY_REDUCTION = 'reduction';
export const CLIENT_PRICING_MARGIN_DISPLAY_RETAINED = 'retained';

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const normalizeRowId = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
};

export const formatClientPricingMoney = (value) =>
  `$${formatPesoAmount(Number(value || 0))}`;

export const normalizeClientPricingDiscountValueInput = (value) => {
  const digits = String(value ?? '').replace(/[^\d]/g, '');
  const withoutLeadingZeros = digits.replace(/^0+(?=\d)/, '');
  return withoutLeadingZeros === '0' ? '' : withoutLeadingZeros;
};

export const normalizeClientPricingMarginPercentInput = (value) => {
  const normalized = String(value ?? '').replace(',', '.').replace(/[^\d.]/g, '');
  const [rawWhole = '', ...decimalParts] = normalized.split('.');
  const whole = rawWhole.replace(/^0+(?=\d)/, '') || (normalized ? '0' : '');
  if (!decimalParts.length) return whole;
  return `${whole}.${decimalParts.join('').slice(0, 4)}`;
};

const formatClientPricingPercentValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  return numeric.toFixed(4).replace(/\.?0+$/, '');
};

export const normalizeClientPricingMarginDisplay = (value) => (
  value === CLIENT_PRICING_MARGIN_DISPLAY_RETAINED
    ? CLIENT_PRICING_MARGIN_DISPLAY_RETAINED
    : CLIENT_PRICING_MARGIN_DISPLAY_REDUCTION
);

export const getClientPricingMarginReductionPercent = (rule = {}) => {
  if (rule?.marginReductionPercent === null
    || rule?.marginReductionPercent === undefined
    || rule?.marginReductionPercent === '') return null;
  const value = Number(rule?.marginReductionPercent);
  if (!Number.isFinite(value)) return null;
  return Math.min(Math.max(value, 0), 100);
};

export const isClientPricingMarginRule = (rule = {}) => (
  rule?.ruleType === CLIENT_PRICING_RULE_FIXED
    ? false
    : rule?.ruleType === CLIENT_PRICING_RULE_MARGIN
      || (rule?.marginReductionPercent !== null
      && rule?.marginReductionPercent !== undefined
      && rule?.marginReductionPercent !== '')
);

export const hasClientPricingRule = (rule = {}) => {
  if (isClientPricingMarginRule(rule)) {
    return Number(getClientPricingMarginReductionPercent(rule) || 0) > 0;
  }
  return Number(rule?.discountValue || 0) > 0;
};

export const marginReductionPercentToDisplay = (
  marginReductionPercent,
  display = CLIENT_PRICING_MARGIN_DISPLAY_REDUCTION
) => {
  const canonical = getClientPricingMarginReductionPercent({ marginReductionPercent });
  if (canonical === null) return '';
  const displayed = normalizeClientPricingMarginDisplay(display) === CLIENT_PRICING_MARGIN_DISPLAY_RETAINED
    ? 100 - canonical
    : canonical;
  return formatClientPricingPercentValue(displayed);
};

export const marginDisplayPercentToReduction = (
  displayPercent,
  display = CLIENT_PRICING_MARGIN_DISPLAY_REDUCTION
) => {
  if (displayPercent === null || displayPercent === undefined || String(displayPercent).trim() === '') return '';
  const value = Number(displayPercent);
  if (!Number.isFinite(value)) return '';
  const canonical = normalizeClientPricingMarginDisplay(display) === CLIENT_PRICING_MARGIN_DISPLAY_RETAINED
    ? 100 - value
    : value;
  return formatClientPricingPercentValue(canonical);
};

export const calculateClientPricingDiscountAmount = ({ basePrice, discountType, discountValue }) => {
  const price = Math.max(0, Math.floor(Number(basePrice || 0)));
  const value = Math.max(0, Number(discountValue || 0));
  const raw = discountType === 'percent'
    ? Math.floor(price * (value / 100))
    : Math.floor(value);
  return Math.min(Math.max(raw, 0), price);
};

const expandClientPricingDecimal = (value) => {
  const source = String(value ?? '').trim();
  if (!/[eE]/.test(source)) return source;
  const numeric = Number(source);
  if (!Number.isFinite(numeric)) return source;
  return numeric.toFixed(12).replace(/0+$/, '').replace(/\.$/, '');
};

const parseClientPricingDecimal = (value) => {
  const source = expandClientPricingDecimal(value);
  const match = source.match(/^([+-]?)(\d+)(?:\.(\d+))?$/);
  if (!match) return null;
  const decimals = match[3] || '';
  const coefficient = BigInt(`${match[1] === '-' ? '-' : ''}${match[2]}${decimals}`);
  return { coefficient, scale: decimals.length };
};

const clientPricingPowerOfTen = (scale) => 10n ** BigInt(scale);

const getClientPricingReferenceCostSource = (referenceCost) => {
  if (typeof referenceCost === 'number' || typeof referenceCost === 'string') {
    return referenceCost === '' ? null : referenceCost;
  }
  const rawValue = referenceCost?.referenceCostRaw;
  if (rawValue !== null && rawValue !== undefined && rawValue !== '') return rawValue;
  const roundedValue = referenceCost?.referenceCost;
  return roundedValue === null || roundedValue === undefined || roundedValue === ''
    ? null
    : roundedValue;
};

export const getClientPricingReferenceCostValue = (referenceCost) => {
  const source = getClientPricingReferenceCostSource(referenceCost);
  if (source === null) return null;
  const numeric = Number(source);
  return Number.isFinite(numeric) ? numeric : null;
};

const calculateExactClientPricingMarginDiscount = ({ price, referenceCost, reductionPercent }) => {
  const cost = parseClientPricingDecimal(getClientPricingReferenceCostSource(referenceCost));
  const rate = parseClientPricingDecimal(reductionPercent);
  if (!cost || !rate) return null;
  const marginScale = cost.scale;
  const marginCoefficient = BigInt(price) * clientPricingPowerOfTen(marginScale)
    - cost.coefficient;
  if (marginCoefficient <= 0n || rate.coefficient <= 0n) return 0;
  const denominator = clientPricingPowerOfTen(marginScale + rate.scale) * 100n;
  return Number((marginCoefficient * rate.coefficient) / denominator);
};

export const calculateClientPricingMarginDiscount = ({
  basePrice,
  referenceCost,
  marginReductionPercent,
}) => {
  const parsedPrice = Number(basePrice);
  const price = Number.isFinite(parsedPrice) ? Math.max(0, Math.floor(parsedPrice)) : 0;
  const cost = getClientPricingReferenceCostValue(referenceCost);
  const reductionPercent = getClientPricingMarginReductionPercent({ marginReductionPercent }) ?? 0;

  if (cost === null || cost < 0) {
    return {
      calculationStatus: 'missing_cost',
      referenceCostValue: null,
      positiveMargin: null,
      discountAmount: 0,
      discountedMargin: null,
      finalPrice: price,
    };
  }

  const rawMargin = price - cost;
  if (!(rawMargin > 0)) {
    return {
      calculationStatus: 'non_positive_margin',
      referenceCostValue: cost,
      positiveMargin: 0,
      discountAmount: 0,
      discountedMargin: 0,
      finalPrice: price,
    };
  }

  const exactDiscountAmount = calculateExactClientPricingMarginDiscount({
    price,
    referenceCost,
    reductionPercent,
  });
  const discountAmount = Math.min(price, Math.max(
    0,
    exactDiscountAmount ?? Math.floor((rawMargin * reductionPercent) / 100)
  ));
  const finalPrice = Math.max(price - discountAmount, 0);
  return {
    calculationStatus: 'applied',
    referenceCostValue: cost,
    positiveMargin: rawMargin,
    discountAmount,
    discountedMargin: Math.max(finalPrice - cost, 0),
    finalPrice,
  };
};

export const calculateClientPricingReferenceMarkup = ({ basePrice, referenceCost }) => {
  const cost = getClientPricingReferenceCostValue(referenceCost);
  const price = Number(basePrice);
  if (cost === null || !Number.isFinite(price)) {
    return { amount: null, percent: null };
  }
  const amount = price - cost;
  return {
    amount,
    percent: cost > 0 ? (amount / cost) * 100 : null,
  };
};

export const calculateClientPricingFinalPriceFromBenefit = ({
  referenceCost,
  benefitType = 'amount',
  benefitValue,
}) => {
  const cost = getClientPricingReferenceCostValue(referenceCost);
  const value = Number(benefitValue);
  if (cost === null || cost < 0 || !Number.isFinite(value) || value < 0) return null;
  const benefitAmount = benefitType === 'percent' ? cost * (value / 100) : value;
  return Math.max(0, Math.ceil(cost + benefitAmount));
};

export const calculateClientPricingDiscountFromFinalPrice = ({ basePrice, finalPrice }) => {
  const price = Math.max(0, Math.floor(Number(basePrice || 0)));
  const target = Math.max(0, Math.floor(Number(finalPrice || 0)));
  return Math.max(price - target, 0);
};

export const toClientPricingDiscountMap = (discounts = []) => {
  const map = new Map();
  (Array.isArray(discounts) ? discounts : []).forEach((discount) => {
    const varietyPriceId = normalizeRowId(discount?.varietyPriceId);
    if (!Number.isInteger(varietyPriceId)) return;
    if (isClientPricingMarginRule(discount)) {
      map.set(varietyPriceId, {
        varietyPriceId,
        ruleType: CLIENT_PRICING_RULE_MARGIN,
        marginReductionPercent: discount.marginReductionPercent ?? '',
      });
      return;
    }
    map.set(varietyPriceId, {
      varietyPriceId,
      ruleType: CLIENT_PRICING_RULE_FIXED,
      discountType: discount.discountType || 'amount',
      discountValue: discount.discountValue ?? '',
      ...(discount.replaceExistingRule === true ? { replaceExistingRule: true } : {}),
    });
  });
  return map;
};

export const mapClientPricingDiscountsToList = (map) => Array.from(map.values());

export const mapClientPricingCatalogRowsToDiscounts = (rows = []) => {
  const map = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const varietyPriceId = normalizeRowId(row?.varietyPriceId);
    if (!Number.isInteger(varietyPriceId) || !hasClientPricingRule(row)) return;
    if (isClientPricingMarginRule(row)) {
      map.set(varietyPriceId, {
        varietyPriceId,
        ruleType: CLIENT_PRICING_RULE_MARGIN,
        marginReductionPercent: row.marginReductionPercent,
      });
      return;
    }
    map.set(varietyPriceId, {
      varietyPriceId,
      ruleType: CLIENT_PRICING_RULE_FIXED,
      discountType: row.discountType || 'amount',
      discountValue: row.discountValue,
      ...(row.replaceExistingRule === true ? { replaceExistingRule: true } : {}),
    });
  });
  return mapClientPricingDiscountsToList(map);
};

export const mergeClientPricingRowsWithDrafts = ({
  rows = [],
  discounts = [],
  draftReady = true,
}) => {
  const customDiscountMap = toClientPricingDiscountMap(discounts);
  return (Array.isArray(rows) ? rows : []).map((row) => {
    const draft = customDiscountMap.get(Number(row.varietyPriceId));
    const useServerDiscount = !draftReady && hasClientPricingRule(row);
    const selectedRule = draft || (useServerDiscount ? row : null);
    const marginRule = isClientPricingMarginRule(selectedRule);
    const discountType = marginRule ? 'amount' : selectedRule?.discountType || 'amount';
    const discountValue = marginRule ? 0 : selectedRule?.discountValue || 0;
    const marginReductionPercent = marginRule ? selectedRule?.marginReductionPercent : null;
    const state = getClientPricingRowState({
      ...row,
      ruleType: marginRule ? CLIENT_PRICING_RULE_MARGIN : CLIENT_PRICING_RULE_FIXED,
      discountType,
      discountValue,
      marginReductionPercent,
    });
    return {
      ...row,
      ruleType: marginRule ? CLIENT_PRICING_RULE_MARGIN : CLIENT_PRICING_RULE_FIXED,
      discountType,
      discountValue,
      marginReductionPercent,
      ...state,
    };
  });
};

export const applyClientPricingBulkDraft = ({
  discounts = [],
  rows = [],
  action = 'apply',
  discountType = 'amount',
  discountValue = '',
  ruleType = CLIENT_PRICING_RULE_FIXED,
  marginReductionPercent = '',
  replaceExistingRules = false,
}) => {
  const nextMap = toClientPricingDiscountMap(discounts);
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const id = normalizeRowId(row?.varietyPriceId);
    if (!Number.isInteger(id)) return;
    const current = nextMap.get(id) || row;
    if (action === 'clear') {
      if (isClientPricingMarginRule(current)) {
        // Custom-client saves replace the whole fixed-rule set but deliberately
        // retain omitted dynamic rules. Keep an explicit zero tombstone so the
        // backend can distinguish "clear this rule" from "leave it untouched".
        nextMap.set(id, {
          varietyPriceId: id,
          ruleType: CLIENT_PRICING_RULE_MARGIN,
          marginReductionPercent: 0,
        });
      } else {
        nextMap.delete(id);
      }
      return;
    }
    if (ruleType === CLIENT_PRICING_RULE_MARGIN) {
      if (Number(marginReductionPercent || 0) > 0) {
        nextMap.set(id, {
          varietyPriceId: id,
          ruleType: CLIENT_PRICING_RULE_MARGIN,
          marginReductionPercent,
        });
      } else if (isClientPricingMarginRule(current)) {
        nextMap.set(id, {
          varietyPriceId: id,
          ruleType: CLIENT_PRICING_RULE_MARGIN,
          marginReductionPercent: 0,
        });
      } else {
        nextMap.delete(id);
      }
      return;
    }
    const replacingDynamicRule = isClientPricingMarginRule(current);
    nextMap.set(id, {
      varietyPriceId: id,
      ruleType: CLIENT_PRICING_RULE_FIXED,
      discountType: discountType || 'amount',
      discountValue,
      ...(replaceExistingRules && replacingDynamicRule ? { replaceExistingRule: true } : {}),
    });
  });
  return mapClientPricingDiscountsToList(nextMap);
};

export const buildClientPricingCategoryOptions = (rows = []) => {
  const options = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const rawId = row?.categoryId ?? 'uncategorized';
    const id = String(rawId || 'uncategorized');
    const label = row?.categoryName || (row?.categoryId ? `Categoria ${row.categoryId}` : 'Sin categoria');
    if (!options.has(id)) {
      options.set(id, {
        id,
        label,
        initial: String(label || id).trim().charAt(0).toUpperCase(),
      });
    }
  });
  return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label));
};

export const filterClientPricingRows = (rows = [], {
  search = '',
  categoryId = '',
  clientType = '',
  searchMode,
} = {}) => {
  const terms = normalizeText(search).split(/\s+/).filter(Boolean);
  const categoryFilter = String(categoryId ?? '').trim();
  const typeFilter = normalizeProductFormatCode(clientType);
  if (!categoryFilter || !typeFilter) return [];

  const scopedRows = (Array.isArray(rows) ? rows : []).filter((row) => {
    if (String(row?.categoryId ?? 'uncategorized') !== categoryFilter) return false;
    if (normalizeProductFormatCode(row?.productFormatCode || row?.clientType) !== typeFilter) return false;
    return true;
  });

  if (!terms.length) return scopedRows;

  return orderFieldSearchResults(scopedRows, search, (row) => [
    row?.productName,
    row?.varietyName,
    row?.barcode,
    row?.metricUnit,
    row?.description,
    row?.categoryName,
    row?.productFormatName,
    row?.clientTypeLabel,
  ], { searchMode });
};

const sortRows = (rows) => [...rows].sort((a, b) => {
  const productCompare = String(a.productName || '').localeCompare(String(b.productName || ''));
  if (productCompare !== 0) return productCompare;
  const varietyCompare = String(a.varietyName || '').localeCompare(String(b.varietyName || ''));
  if (varietyCompare !== 0) return varietyCompare;
  const typeA = CLIENT_TYPE_OPTIONS.findIndex((option) => option.value === a.clientType);
  const typeB = CLIENT_TYPE_OPTIONS.findIndex((option) => option.value === b.clientType);
  return (typeA === -1 ? 99 : typeA) - (typeB === -1 ? 99 : typeB);
});

export const buildClientPricingProductGroups = (rows = []) => {
  const productGroups = new Map();
  sortRows(Array.isArray(rows) ? rows : []).forEach((row) => {
    const productKey = row.productId || row.productName || 'unknown-product';
    const varietyKey = `${productKey}:${row.varietyId || row.varietyName || 'unknown-variety'}`;
    if (!productGroups.has(productKey)) {
      productGroups.set(productKey, {
        key: productKey,
        productId: row.productId,
        productName: row.productName,
        productImage: row.productImage,
        categoryId: row.categoryId,
        categoryName: row.categoryName,
        variants: new Map(),
        priceRows: [],
      });
    }
    const productGroup = productGroups.get(productKey);
    if (!productGroup.variants.has(varietyKey)) {
      productGroup.variants.set(varietyKey, {
        key: varietyKey,
        varietyId: row.varietyId,
        varietyName: row.varietyName,
        varietyImage: row.varietyImage,
        priceRows: [],
      });
    }
    productGroup.variants.get(varietyKey).priceRows.push(row);
    productGroup.priceRows.push(row);
  });
  return Array.from(productGroups.values()).map((group) => ({
    ...group,
    variants: Array.from(group.variants.values()),
  }));
};

export const getClientPricingSearchProducts = (rows = []) => (
  buildClientPricingProductGroups(rows).map((group) => ({
    productName: group.productName,
    Varieties: group.variants.map((variant) => ({
      varietyName: variant.varietyName,
      VarietyPrices: variant.priceRows.map((row) => ({ barcode: row.barcode })),
    })),
  }))
);

export const getClientPricingRowState = (row) => {
  const referenceCost = getClientPricingReferenceCostValue(row?.referenceCost) === null
    ? row?.referenceCostValue
    : row?.referenceCost;
  if (isClientPricingMarginRule(row)) {
    const state = calculateClientPricingMarginDiscount({
      basePrice: row?.basePrice,
      referenceCost,
      marginReductionPercent: row?.marginReductionPercent,
    });
    return {
      ...state,
      ruleType: CLIENT_PRICING_RULE_MARGIN,
      marginReductionPercent: getClientPricingMarginReductionPercent(row),
      hasDiscount: Number(getClientPricingMarginReductionPercent(row) || 0) > 0,
      clientTypeLabel: row?.clientTypeLabel || getClientTypeLabel(row?.clientType),
    };
  }
  const discountAmount = calculateClientPricingDiscountAmount({
    basePrice: row?.basePrice,
    discountType: row?.discountType || 'amount',
    discountValue: row?.discountValue || 0,
  });
  const finalPrice = Math.max(Number(row?.basePrice || 0) - discountAmount, 0);
  return {
    discountAmount,
    finalPrice,
    hasDiscount: discountAmount > 0,
    ruleType: CLIENT_PRICING_RULE_FIXED,
    marginReductionPercent: null,
    positiveMargin: getClientPricingReferenceCostValue(referenceCost) === null
      ? null
      : Math.max(Number(row?.basePrice || 0) - getClientPricingReferenceCostValue(referenceCost), 0),
    discountedMargin: getClientPricingReferenceCostValue(referenceCost) === null
      ? null
      : Math.max(finalPrice - getClientPricingReferenceCostValue(referenceCost), 0),
    calculationStatus: row?.calculationStatus || 'fixed_discount',
    clientTypeLabel: row?.clientTypeLabel || getClientTypeLabel(row?.clientType),
  };
};
